import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined';
import { formatDate } from '../../../../utils/Utils';
import { setupStepLabel } from '../../../setup-wizard/constants/setupSteps';

/**
 * Formata uma data 'Y-m-d H:i:s' usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

// Mesmas chaves de SetupProgress::STATUS_KEYS (backend)
const STATUS_CONFIG = {
  not_started: { label: 'Não iniciou', backgroundColor: '#F5F5F5', color: '#616161' },
  in_progress: { label: 'Em andamento', backgroundColor: '#E3F2FD', color: '#1565C0' },
  completed: { label: 'Concluída', backgroundColor: '#E8F5E9', color: '#2E7D32' },
  skipped: { label: 'Pulada', backgroundColor: '#FFF3E0', color: '#E65100' },
};

const STEP_STATE_CONFIG = {
  completed: { label: 'Concluída', dot: '#43A047' },
  visited: { label: 'Acessada, não concluída', dot: '#FB8C00' },
  pending: { label: 'Não acessada', dot: '#BDBDBD' },
};

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function StepRow({ step }) {
  const cfg = STEP_STATE_CONFIG[step.state] || STEP_STATE_CONFIG.pending;
  const date = step.state === 'completed' ? step.completed_at : step.last_visited_at;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flex: 1 }}>
        {setupStepLabel(step.key)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
        {cfg.label}
        {date ? ` · ${fmtDate(date)}` : ''}
      </Typography>
    </Box>
  );
}

function summaryRows(setup) {
  const lastStepLabel = setup.last_step ? setupStepLabel(setup.last_step) : null;
  const finishedLabel = setup.status === 'skipped' ? 'Pulada em' : 'Concluída em';

  return [
    { label: 'Progresso', value: `${setup.completed_steps ?? 0} de ${setup.total_steps ?? 5} etapas concluídas` },
    { label: 'Última etapa acessada', value: lastStepLabel ? `${lastStepLabel} · ${fmtDate(setup.last_step_at)}` : '—' },
    { label: 'Iniciado em', value: fmtDate(setup.started_at) },
    { label: 'Último avanço', value: fmtDate(setup.last_progress_at) },
    { label: finishedLabel, value: fmtDate(setup.finished_at) },
  ];
}

/**
 * CompanySetupCard - Card "Configuração inicial" da página da empresa (Admin Geral)
 *
 * Mostra o que a empresa fez no assistente de configuração inicial (wizard):
 * status, progresso, datas e a situação de cada etapa, para enxergar onde o
 * onboarding trava. Os dados vêm de `company.setup` (admin/companies/get.php).
 *
 * @param {Object} props
 * @param {Object|null} props.setup - bloco `setup` da empresa
 */
function CompanySetupCard({ setup }) {
  if (!setup) return null;

  const statusCfg = STATUS_CONFIG[setup.status] || STATUS_CONFIG.not_started;
  const steps = Array.isArray(setup.steps) ? setup.steps : [];
  const hasHistory = steps.some((step) => step.state !== 'pending');
  // Empresa que passou pelo wizard antes do registro por etapa existir.
  const historyUnavailable = !hasHistory && setup.status !== 'not_started';

  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', color: '#5A80FB' }}>
          <ChecklistRtlOutlinedIcon fontSize="small" />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
          Configuração inicial
        </Typography>
        <Chip
          size="small"
          label={statusCfg.label}
          sx={{
            backgroundColor: statusCfg.backgroundColor,
            color: statusCfg.color,
            fontWeight: 500,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {summaryRows(setup).map((row) => (
            <InfoRow key={row.label} label={row.label} value={row.value} />
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Etapas do assistente
          </Typography>
          {historyUnavailable ? (
            <Typography variant="body2" color="text.secondary">
              Sem histórico por etapa: esta empresa passou pelo assistente antes do registro de progresso.
            </Typography>
          ) : (
            steps.map((step) => <StepRow key={step.key} step={step} />)
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default CompanySetupCard;
